import { useMutation } from '@apollo/react-hooks';
import React, { useRef, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { useDuplicateEvent } from 'components/Events/gql';
import { DeleteConfirmModal } from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { DELETE_EVENT } from 'pages/app/Events/CancelModal/CancelModal.mutations';
import { useGetAccount } from '../gql/queries/useGetAccount';
import { useGetAccountEvents } from './gql/useGetAccountEvents';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const HeaderSubtitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-left: 4px;
`;

const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${(props) => {
    switch (props.color) {
      case 'green':
        return '#22c55e';
      case 'yellow':
        return '#eab308';
      case 'red':
        return '#ef4444';
      default:
        return '#94a3b8';
    }
  }};
`;

const StatusText = styled.span`
  text-transform: capitalize;
  font-size: 13px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};

  &:hover {
    color: ${(props) => (props.theme.name === 'dark' ? '#e2e8f0' : '#1e293b')};
  }
`;

const AccountEvents = () => {
  const { accountId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();
  const previousSearchRef = useRef(input.queryString || '');

  // Filter state - sync with URL query params
  const [filters, setFilters] = useState({
    status: input.status || '',
  });

  const filterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'radio',
      options: [
        { label: 'Published', value: 'published' },
        { label: 'Draft', value: 'draft' },
        { label: 'Cancelled', value: 'cancelled' },
        { label: 'Postponed', value: 'postponed' },
      ],
    },
  ];

  const duplicateEvent = useDuplicateEvent();

  const { data: accountData } = useGetAccount(Number(accountId));
  const { data, loading, refetch } = useGetAccountEvents(accountId);

  const account = accountData?.getAccount;
  const accountName = account?.business_dba || account?.legal_name || '';
  const events = data?.getEventsByAccountAdmin?.results ?? [];
  const totalCount = data?.getEventsByAccountAdmin?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    if (searchTerm !== previousSearchRef.current) {
      previousSearchRef.current = searchTerm;
      updateQuery({ queryString: searchTerm, page: 1 });
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    updateQuery({
      status: newFilters.status || undefined,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    setFilters({ status: '' });
    updateQuery({
      status: undefined,
      page: 1,
    });
  };

  const handleBack = () => {
    history.push(`${routePrefix}/accounts`);
  };

  const handleViewEvent = (event) => {
    history.push(`${routePrefix}/events/menu/${event.id}`);
  };

  const handleEditEvent = (event) => {
    history.push(`${routePrefix}/events/editEvent/${event.id}`);
  };

  // Delete event
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [deleteEventMutation] = useMutation(DELETE_EVENT);

  const handleDeleteClick = (event) => {
    setSelectedEvent(event);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedEvent) return;
    setIsDeleting(true);
    try {
      await deleteEventMutation({
        variables: { id: selectedEvent.id },
        refetchQueries: ['GetEventsByAccountAdmin'],
      });
    } catch (err) {
      // mutation error handled silently
    }
    setIsDeleting(false);
    setShowDeleteModal(false);
    setSelectedEvent(null);
  };

  const handleDuplicateClick = (event) => {
    setSelectedEvent(event);
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedEvent) return;
    setIsDuplicating(true);
    const response = await duplicateEvent(
      selectedEvent.id,
      selectedEvent.track?.id ?? null
    );
    if (!response || response.errors) {
      toast.error('Could not duplicate this event');
    } else {
      toast.success('Event successfully duplicated');
      refetch();
    }
    setIsDuplicating(false);
    setShowDuplicateModal(false);
    setSelectedEvent(null);
  };

  const columns = [
    {
      field: 'fullDate',
      header: 'Date',
      render: (event) => {
        if (event.isMultiDay && event.start_date && event.end_date) {
          const parse = (d) => {
            const [mm, dd, yyyy] = d.split('-');
            return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
          };
          const start = parse(event.start_date);
          const end = parse(event.end_date);
          const month = start.toLocaleDateString('en-US', { month: 'short' });
          const startDay = start.getDate();
          const endDay = end.getDate();
          const year = start.getFullYear();
          return `${month} ${startDay}-${endDay}, ${year}`;
        }
        return event.fullDate;
      },
    },
    {
      field: 'name',
      header: 'Event Name',
    },
    {
      field: 'track.name',
      header: 'Track',
      render: (event) => event.track?.name || '-',
    },
    {
      field: 'status',
      header: 'Status',
      render: (event) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <StatusDot color={event.status_color} />
          <StatusText>{event.status || '-'}</StatusText>
        </span>
      ),
    },
  ];

  const actions = [
    {
      type: 'view',
      title: 'View',
      onClick: handleViewEvent,
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditEvent,
    },
    {
      type: 'duplicate',
      title: 'Duplicate',
      onClick: handleDuplicateClick,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  return (
    <EnhancedThemeProvider>
      <div style={{ background: 'transparent', minHeight: '100vh' }}>
        <PageContainer>
          {/* Header Card */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <BackButton onClick={handleBack}>
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </BackButton>
              <PageHeaderIcon icon="events" />
              <HeaderTitle>
                Events
                {accountName && (
                  <HeaderSubtitle>- {accountName}</HeaderSubtitle>
                )}
              </HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Content Card */}
          <Card noPadding>
            <ContentWrapper>
              <PageHeader>
                <SearchInput
                  onSearch={handleSearch}
                  placeholder="Search events by name..."
                />
                <TableFilter
                  filters={filterConfig}
                  values={filters}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                />
                <ButtonV2
                  onClick={() =>
                    history.push({
                      pathname: `${routePrefix}/events/addEvent`,
                      state: { accountId: Number(accountId) },
                    })
                  }
                  style={{ width: 'auto', flexShrink: 0 }}
                >
                  Add Event
                </ButtonV2>
              </PageHeader>

              <DataTable
                columns={columns}
                data={events}
                loading={loading}
                keyField="id"
                emptyMessage="No events found"
                emptySubtext="This account has no events yet"
                emptyIcon={
                  <svg
                    width="48"
                    height="48"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                    />
                  </svg>
                }
                actions={actions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </ContentWrapper>
          </Card>
        </PageContainer>
      </div>
      <DeleteConfirmModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleDuplicateConfirm}
        title="Duplicate Event"
        message={`Are you sure you want to duplicate "${
          selectedEvent?.name || ''
        }"?`}
        confirmText="Duplicate"
        isDeleting={isDuplicating}
      />
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Event"
        message={`Are you sure you want to delete "${
          selectedEvent?.name || ''
        }"? This action cannot be undone.`}
        confirmText="Delete"
        isDeleting={isDeleting}
      />
    </EnhancedThemeProvider>
  );
};

export default AccountEvents;
